import type {
  Stats,
  DetailedStats,
  FormStats,
  LearningInsight,
  ChallengeLevel,
} from "@/types/index";
import type { StorageManager } from "@/utils/storage";
import { eventManager } from "@/utils/event-manager";

/**
 * Interface for accuracy history entries
 */
interface AccuracyHistoryEntry {
  timestamp: number;
  isCorrect: boolean;
  accuracy: number;
}

/**
 * Enhanced form statistics with type safety
 */
interface EnhancedFormStats extends FormStats {
  lastPracticed: number | null;
  accuracyHistory: AccuracyHistoryEntry[];
}

/**
 * Interface for detailed stats with enhanced form stats
 */
interface EnhancedDetailedStats {
  [tense: string]: {
    [pronoun: string]: EnhancedFormStats;
  };
}

/**
 * Type-safe statistics tracking with advanced analytics
 */
export class StatsTracker {
  // @ts-ignore TS6133: Reserved for future use
  private readonly _verbName: string; // Reserved for future use
  private readonly storage: StorageManager;
  private stats: Stats;
  private formStats: EnhancedDetailedStats;

  constructor(verbName: string, storageManager: StorageManager) {
    this._verbName = verbName;
    this.storage = storageManager;
    this.stats = this.loadStats();
    this.formStats = this.loadFormStats();
  }

  /**
   * Load overall statistics from storage
   */
  private loadStats(): Stats {
    return (
      this.storage.load("stats", {
        correct: 0,
        total: 0,
        accuracy: 0,
      }) || { correct: 0, total: 0, accuracy: 0 }
    );
  }

  /**
   * Load form-specific statistics from storage
   */
  private loadFormStats(): EnhancedDetailedStats {
    return this.storage.load("form-stats", {}) || {};
  }

  /**
   * Record an answer with comprehensive tracking
   * @param isCorrect - Whether the answer was correct
   * @param tense - The verb tense
   * @param pronoun - The pronoun used
   */
  recordAnswer(isCorrect: boolean, tense: string, pronoun: string): void {
    // Update overall stats
    this.stats.total++;
    if (isCorrect) {
      this.stats.correct++;
    }
    this.stats.accuracy =
      this.stats.total > 0
        ? Math.round((this.stats.correct / this.stats.total) * 100)
        : 0;

    // Update form-specific stats
    if (!this.formStats[tense]) {
      this.formStats[tense] = {};
    }
    if (!this.formStats[tense][pronoun]) {
      this.formStats[tense][pronoun] = {
        correct: 0,
        total: 0,
        accuracy: 0,
        timeWeightedAccuracy: 0,
        lastPracticed: null,
        accuracyHistory: [],
        attempts: [],
      };
    }

    const formStat = this.formStats[tense][pronoun];
    formStat.total++;
    if (isCorrect) {
      formStat.correct++;
    }

    // Update accuracy
    formStat.accuracy = Math.round((formStat.correct / formStat.total) * 100);

    // Update time-weighted accuracy (more recent answers have more weight)
    this.updateTimeWeightedAccuracy(formStat, isCorrect);

    // Update last practiced
    formStat.lastPracticed = Date.now();

    // Add to attempts array for spaced repetition
    formStat.attempts.push({
      isCorrect,
      timestamp: Date.now(),
    });

    // Keep only last 100 attempts for performance
    if (formStat.attempts.length > 100) {
      formStat.attempts = formStat.attempts.slice(-100);
    }

    // Add to accuracy history
    formStat.accuracyHistory.push({
      timestamp: Date.now(),
      isCorrect: isCorrect,
      accuracy: formStat.accuracy,
    });

    // Keep only last 50 entries
    if (formStat.accuracyHistory.length > 50) {
      formStat.accuracyHistory = formStat.accuracyHistory.slice(-50);
    }

    // Save to storage
    this.saveStats();
    this.saveFormStats();

    // Emit typed event
    eventManager.emit("stats-updated", this.getStats());
  }

  /**
   * Update time-weighted accuracy with decay function
   * @private
   */
  private updateTimeWeightedAccuracy(
    formStat: EnhancedFormStats,
    isCorrect: boolean
  ): void {
    const weight = 0.3; // How much to weight the current answer
    const currentScore = isCorrect ? 100 : 0;

    if (formStat.timeWeightedAccuracy === 0) {
      formStat.timeWeightedAccuracy = currentScore;
    } else {
      formStat.timeWeightedAccuracy = Math.round(
        formStat.timeWeightedAccuracy * (1 - weight) + currentScore * weight
      );
    }
  }

  /**
   * Get overall statistics
   */
  getStats(): Stats {
    return { ...this.stats };
  }

  /**
   * Get detailed form-specific statistics
   */
  getDetailedStats(): DetailedStats {
    // Convert enhanced stats to standard format for compatibility
    const result: DetailedStats = {};

    for (const [tense, pronounStats] of Object.entries(this.formStats)) {
      result[tense] = {};
      for (const [pronoun, stats] of Object.entries(pronounStats)) {
        result[tense][pronoun] = {
          correct: stats.correct,
          total: stats.total,
          accuracy: stats.accuracy,
          timeWeightedAccuracy: stats.timeWeightedAccuracy,
          attempts: stats.attempts,
        };
      }
    }

    return result;
  }

  /**
   * Get statistics for a specific form
   */
  getFormStats(tense: string, pronoun: string): FormStats {
    const formStat = this.formStats[tense]?.[pronoun];
    if (!formStat) {
      return {
        correct: 0,
        total: 0,
        accuracy: 0,
        timeWeightedAccuracy: 0,
        attempts: [],
      };
    }

    return {
      correct: formStat.correct,
      total: formStat.total,
      accuracy: formStat.accuracy,
      timeWeightedAccuracy: formStat.timeWeightedAccuracy,
      attempts: formStat.attempts,
    };
  }

  /**
   * Get enhanced statistics for a specific form (includes accuracyHistory and lastPracticed)
   */
  getEnhancedFormStats(tense: string, pronoun: string): EnhancedFormStats {
    const formStat = this.formStats[tense]?.[pronoun];
    if (!formStat) {
      return {
        correct: 0,
        total: 0,
        accuracy: 0,
        timeWeightedAccuracy: 0,
        attempts: [],
        lastPracticed: null,
        accuracyHistory: [],
      };
    }

    return formStat; // Return the full enhanced stats object
  }

  /**
   * Get learning insights based on performance patterns
   */
  getLearningInsights(): LearningInsight[] {
    const insights: LearningInsight[] = [];
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    // Analyze struggling forms
    const strugglingForms: Array<{
      form: string;
      reason: string;
      priority: "urgent" | "high" | "normal";
    }> = [];
    const improvingForms: Array<{
      form: string;
      reason: string;
      priority: "urgent" | "high" | "normal";
    }> = [];
    const masteredForms: Array<{
      form: string;
      reason: string;
      priority: "urgent" | "high" | "normal";
    }> = [];

    for (const [tense, pronounStats] of Object.entries(this.formStats)) {
      for (const [pronoun, stats] of Object.entries(pronounStats)) {
        const form = `${tense} - ${pronoun}`;

        if (stats.total < 3) continue; // Skip forms with insufficient data

        // Check for struggling patterns
        if (stats.accuracy < 60) {
          strugglingForms.push({
            form,
            reason: `${stats.accuracy}% accuracy needs improvement`,
            priority: stats.accuracy < 40 ? "urgent" : "high",
          });
        }

        // Check for forgetting curve (good accuracy but not practiced recently)
        const daysSinceLastPractice = stats.lastPracticed
          ? (now - stats.lastPracticed) / dayMs
          : Infinity;

        if (stats.accuracy >= 80 && daysSinceLastPractice > 7) {
          strugglingForms.push({
            form,
            reason: `Not practiced for ${Math.floor(
              daysSinceLastPractice
            )} days`,
            priority: "normal",
          });
        }

        // Check for improving trends
        if (stats.accuracyHistory.length >= 5) {
          const recentAccuracy =
            stats.accuracyHistory
              .slice(-3)
              .reduce((sum, entry) => sum + (entry.isCorrect ? 100 : 0), 0) / 3;
          const olderAccuracy =
            stats.accuracyHistory
              .slice(-6, -3)
              .reduce((sum, entry) => sum + (entry.isCorrect ? 100 : 0), 0) / 3;

          if (recentAccuracy > olderAccuracy + 20) {
            improvingForms.push({
              form,
              reason: `Improving trend: ${Math.round(
                olderAccuracy
              )}% → ${Math.round(recentAccuracy)}%`,
              priority: "normal",
            });
          }
        }

        // Check for mastered forms
        if (stats.accuracy >= 90 && stats.total >= 10) {
          masteredForms.push({
            form,
            reason: `${stats.accuracy}% accuracy over ${stats.total} attempts`,
            priority: "normal",
          });
        }
      }
    }

    // Add insights if we have data
    if (strugglingForms.length > 0) {
      insights.push({
        type: "struggling",
        title: "Forms that need attention",
        items: strugglingForms
          .sort((a, b) => {
            const priorityOrder = { urgent: 0, high: 1, normal: 2 };
            return priorityOrder[a.priority] - priorityOrder[b.priority];
          })
          .slice(0, 5),
      });
    }

    if (improvingForms.length > 0) {
      insights.push({
        type: "improving",
        title: "Great progress on these forms",
        items: improvingForms.slice(0, 3),
      });
    }

    if (masteredForms.length > 0) {
      insights.push({
        type: "mastered",
        title: "Well mastered forms",
        items: masteredForms.slice(0, 3),
      });
    }

    return insights;
  }

  /**
   * Get challenge progress for progressive difficulty
   */
  getChallengeProgress(): Record<string, Record<string, ChallengeLevel>> {
    const progress: Record<string, Record<string, ChallengeLevel>> = {};

    for (const [tense, pronounStats] of Object.entries(this.formStats)) {
      progress[tense] = {};
      for (const [pronoun, stats] of Object.entries(pronounStats)) {
        let level = 1;
        let name = "Beginner";

        if (stats.total >= 5 && stats.accuracy >= 70) {
          level = 2;
          name = "Intermediate";
        }
        if (stats.total >= 10 && stats.accuracy >= 85) {
          level = 3;
          name = "Advanced";
        }

        progress[tense][pronoun] = { level, name };
      }
    }

    return progress;
  }

  /**
   * Get accuracy trend for a form
   */
  getAccuracyTrend(tense: string, pronoun: string): number {
    const formStat = this.getEnhancedFormStats(tense, pronoun);
    if (formStat.accuracyHistory.length < 5) {
      return 0; // Not enough data
    }

    const recent = formStat.accuracyHistory.slice(-5);
    const older = formStat.accuracyHistory.slice(-10, -5);

    if (older.length === 0) return 0;

    const recentAvg =
      recent.reduce((sum, entry) => sum + entry.accuracy, 0) / recent.length;
    const olderAvg =
      older.reduce((sum, entry) => sum + entry.accuracy, 0) / older.length;

    return recentAvg - olderAvg;
  }

  /**
   * Get last practiced time for a form
   */
  getLastPracticed(tense: string, pronoun: string): number | null {
    const formStat = this.getEnhancedFormStats(tense, pronoun);
    return formStat.lastPracticed;
  }

  /**
   * Get count of mastered forms
   */
  getMasteredFormsCount(threshold: number = 80): number {
    let count = 0;
    for (const tense of Object.values(this.formStats)) {
      for (const stats of Object.values(tense)) {
        if (stats.total >= 5 && stats.accuracy >= threshold) {
          count++;
        }
      }
    }
    return count;
  }

  /**
   * Get days since first practice session
   */
  getDaysSinceFirstPractice(): number {
    let earliestDate: number | null = null;

    for (const tense of Object.values(this.formStats)) {
      for (const stats of Object.values(tense)) {
        if (
          stats.lastPracticed &&
          (!earliestDate || stats.lastPracticed < earliestDate)
        ) {
          earliestDate = stats.lastPracticed;
        }
      }
    }

    if (!earliestDate) return 0;

    const daysSince = (Date.now() - earliestDate) / (1000 * 60 * 60 * 24);
    return Math.max(1, Math.floor(daysSince)); // At least 1 day
  }
  /**
   * Reset statistics based on options
   */
  reset(options: {
    resetStats?: boolean;
    resetStreaks?: boolean;
    resetXP?: boolean;
    resetAchievements?: boolean;
  }): void {
    if (options.resetStats) {
      this.stats = { correct: 0, total: 0, accuracy: 0 };
      this.formStats = {};
      this.saveStats();
      this.saveFormStats();
    }

    // Note: resetStreaks, resetXP, and resetAchievements are handled
    // by their respective systems (Gamification and Achievements)
  }

  /**
   * Save overall stats to storage
   * @private
   */
  private saveStats(): void {
    this.storage.save("stats", this.stats);
  }

  /**
   * Save form stats to storage
   * @private
   */
  private saveFormStats(): void {
    this.storage.save("form-stats", this.formStats);
  }
}

// Make StatsTracker globally available
declare global {
  interface Window {
    StatsTracker: typeof StatsTracker;
  }
}

window.StatsTracker = StatsTracker;
